import React, { createContext, useContext, useCallback, ReactNode } from 'react';
import { ethers } from 'ethers';
import { useLocation, useNavigate } from 'react-router-dom';
import { getNetworkByChainId } from '../config/networks';

interface EscrowContextType {
  contractAddress: string;
  chainId: string;
  setContractAddress: (address: string, chainId?: string) => void;
  clearContractAddress: () => void;
  isValidAddress: (address: string) => boolean;
  isAddressSet: boolean;
  navigateToEscrow: (path: string) => void;
}

const EscrowContext = createContext<EscrowContextType | undefined>(undefined);

export const useEscrow = () => {
  const context = useContext(EscrowContext);
  if (context === undefined) {
    throw new Error('useEscrow must be used within an EscrowProvider');
  }
  return context;
};

interface EscrowProviderProps {
  children: ReactNode;
}

export const EscrowProvider: React.FC<EscrowProviderProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Extract query parameters
  const searchParams = new URLSearchParams(location.search);
  const contractAddress = searchParams.get('contractAddress') || '';
  const chainId = searchParams.get('chainId') || '';

  const setContractAddress = useCallback((address: string, newChainId?: string) => {
    // Validate address
    if (!address || !ethers.isAddress(address)) {
      console.error('Invalid contract address provided');
      navigate('/');
      return;
    }
    
    // Determine and validate chainId
    const targetChainId = newChainId || chainId;
    if (!targetChainId) {
      console.error('No chain ID provided or available in URL');
      navigate('/');
      return;
    }
    
    // Validate chainId is a valid positive number
    const parsedChainId = parseInt(targetChainId, 10);
    if (isNaN(parsedChainId) || parsedChainId <= 0) {
      console.error('Invalid chain ID:', targetChainId);
      navigate('/');
      return;
    }
    
    // Validate chainId is a supported network
    const network = getNetworkByChainId(parsedChainId);
    if (!network) {
      console.error('Unsupported chain ID:', parsedChainId);
      navigate('/');
      return;
    }
    
    // Update URL with query parameters
    const newSearchParams = new URLSearchParams();
    newSearchParams.set('chainId', targetChainId);
    newSearchParams.set('contractAddress', address);
    navigate(`/?${newSearchParams.toString()}`);
  }, [chainId, navigate]);

  const clearContractAddress = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const isValidAddress = useCallback((address: string) => {
    return ethers.isAddress(address);
  }, []);

  const navigateToEscrow = useCallback((path: string) => {
    if (contractAddress && chainId) {
      const newSearchParams = new URLSearchParams();
      newSearchParams.set('chainId', chainId);
      newSearchParams.set('contractAddress', contractAddress);
      navigate(`${path}?${newSearchParams.toString()}`);
    } else {
      navigate('/');
    }
  }, [contractAddress, chainId, navigate]);

  const isAddressSet = contractAddress !== '';

  const value: EscrowContextType = {
    contractAddress,
    chainId,
    setContractAddress,
    clearContractAddress,
    isValidAddress,
    isAddressSet,
    navigateToEscrow,
  };

  return (
    <EscrowContext.Provider value={value}>
      {children}
    </EscrowContext.Provider>
  );
};
